// The equip/de-equip transition (design locked in the Celebration Lab):
// a neon scanline sweeps the card and the state changes PROGRESSIVELY along
// it - when the line is 30% down, 30% of the card is in the new state.
// Equipping sweeps DOWN in green; de-equipping sweeps UP in the card's own
// tier color.
//
// Mechanism: a frozen snapshot clone of the old state is laid over the card
// INSIDE the card's own slot, so it is styled by exactly the same rules as
// the card (a clone parked on <body> lost the phone layout's styles and
// showed a different card mid-wipe), scrolls with it, sits under the shop's
// sticky chrome, and takes the same hover zoom. React flips the real card
// underneath; the clone melts away along the traveling line.
import { RM } from "./purchase-celebration";

export function runEquipTransition(itemId: string, equipping: boolean, flip: () => void): void {
  const slot = document.querySelector<HTMLElement>(`[data-packid="${itemId}"]`);
  const pack = slot?.querySelector<HTMLElement>("[data-pack]");
  if (!slot || !pack || RM) { flip(); return; }

  // The overlay box: the card's own place in the slot, to the fraction of a
  // pixel. (offset* round to whole pixels, and a card that sits on a half pixel
  // then showed its frozen copy a pixel off: the picture and the text jumped as
  // the wipe began and again as it ended.) Measured on screen and divided by the
  // slot's own scale, so a hover zoom on the slot does not skew it.
  if (getComputedStyle(slot).position === "static") slot.style.position = "relative";
  const slotBox = slot.getBoundingClientRect(), packBox = pack.getBoundingClientRect();
  const zoom = slot.offsetWidth > 0 ? slotBox.width / slot.offsetWidth : 1;
  const k = zoom > 0 ? zoom : 1;
  const frame = document.createElement("div");
  frame.style.cssText = `position:absolute; left:${(packBox.left - slotBox.left) / k - slot.clientLeft}px; top:${(packBox.top - slotBox.top) / k - slot.clientTop}px; width:${packBox.width / k}px; height:${packBox.height / k}px; z-index:2; pointer-events:none;`;
  // the card's own computed size, so the copy lays out exactly as the card does
  // whatever its box-sizing (100% of the frame would add its padding and border on top)
  const packStyle = getComputedStyle(pack);

  // --- the frozen clone of the old state ---
  const clone = pack.cloneNode(true) as HTMLElement;
  // not a second card: nothing that looks for the card may find the clone
  clone.removeAttribute("data-pack");
  clone.removeAttribute("id");
  clone.querySelectorAll("[id]").forEach((n) => n.removeAttribute("id"));
  clone.setAttribute("aria-hidden", "true");
  // Bake each button's computed colours in at snapshot time so the frozen
  // state matches what was on screen whatever the pointer does next.
  const liveButtons = pack.querySelectorAll("button");
  clone.querySelectorAll("button").forEach((b, i) => {
    const cs = liveButtons[i] ? getComputedStyle(liveButtons[i]) : null;
    if (cs) { b.style.background = cs.background; b.style.color = cs.color; b.style.borderColor = cs.borderColor; }
  });
  clone.querySelectorAll("canvas").forEach((c, i) => {
    const src = pack.querySelectorAll("canvas")[i];
    if (src) { c.width = src.width; c.height = src.height; c.getContext("2d")?.drawImage(src, 0, 0); }
  });
  clone.style.position = "absolute"; clone.style.inset = "0";
  clone.style.inset = "0 auto auto 0";
  clone.style.width = packStyle.width; clone.style.height = packStyle.height; clone.style.margin = "0";
  // The card under the pointer is zoomed (the hover's scale(1.035), 1.06 for
  // the top tier) and the frame covers that zoomed box, so the copy wears the
  // same zoom: drawn at the card's own size it sat a few px small, its edges
  // doubled mid-wipe and the button jumped when it went.
  const w = parseFloat(packStyle.width) || pack.offsetWidth, h = parseFloat(packStyle.height) || pack.offsetHeight;
  clone.style.transformOrigin = "0 0";
  clone.style.transform = w > 0 && h > 0 ? `scale(${packBox.width / k / w}, ${packBox.height / k / h})` : "none";
  clone.style.transition = "none";
  // The card washes are translucent gradients; back the clone with the panel
  // colour so the new state can't shine through the old one mid-wipe.
  clone.style.backgroundColor = "#0e1422";
  frame.appendChild(clone);
  slot.appendChild(frame);

  flip(); // React repaints the real card in its new state beneath the clone

  const dur = 640;
  const ease = "cubic-bezier(.4,0,.4,1)";
  // The clip-path animates in PERCENT, so it tracks the card's own height.
  const wipe = equipping
    ? [{ clipPath: "inset(0 0 0% 0)" }, { clipPath: "inset(100% 0 0 0)" }]   // old melts away downward
    : [{ clipPath: "inset(0% 0 0 0)" }, { clipPath: "inset(0 0 100% 0)" }];  // old melts away upward
  clone.animate(wipe, { duration: dur, easing: ease, fill: "forwards" });

  // --- the neon line: hot core in the line's own colour, never white-washed ---
  const tierRgb = getComputedStyle(pack).getPropertyValue("--g").trim() || "137, 149, 179";
  const color = equipping ? "#46d97e" : `rgb(${tierRgb})`;
  const line = document.createElement("div");
  line.style.cssText = `position:absolute; left:2px; right:2px; height:3px; border-radius:2px; background:linear-gradient(180deg, ${color}, color-mix(in srgb, ${color} 55%, white) 50%, ${color}); box-shadow:0 0 6px ${color}, 0 0 14px ${color}; pointer-events:none;`;
  // it rides the clone's edge in percent, so it stays on the card whatever its size
  frame.appendChild(line);
  line.animate(
    equipping ? [{ top: "0%" }, { top: "100%" }] : [{ top: "100%" }, { top: "0%" }],
    { duration: dur, easing: ease, fill: "forwards" },
  );
  line.animate([{ opacity: 1, offset: 0.85 }, { opacity: 0 }], { duration: dur, fill: "forwards" });

  setTimeout(() => frame.remove(), dur + 60);
}
